import { useFeatureGate } from '@statsig/react-bindings';
import { differenceInMinutes, parseISO } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useContext, useEffect, useRef } from 'react';

import { MeasureSchemaType } from '@grow-therapy-team/seedling-components';

import { TelehealthSessionInfoData_TelehealthSessionInfo_Appointment } from '../../hooks';
import { Statsig, StatsigLoadingContext } from '../../statsig/StatsigProvider';
import {
  delayedEntryGate,
  preSessionReflectionsGate,
  telehealth4MinMeasureRestrictionDisabledGate,
  telehealthAutoOpenIncompleteMeasuresDrawer,
} from '../../statsig/gates';
import { recordedMoodCheckin } from '../components/forms-drawer/mood-check-in/useMoodCheckinSelectionsState';
import { useGetShouldShowReflectionTopics } from '../reflections/useGetShouldShowReflectionTopics';
import {
  VisitorDrawerState,
  doesClientRequireSafetyGuardsAtom,
  hasCompletedClientMeasuresAtom,
  visitorMeasuresAtom,
} from '../state';
import { JoinSessionCalloutWrapper as JoinSessionCallout } from './delayed-entry/JoinSessionCallout';
import { deadlineAtom } from './delayed-entry/state';
import {
  PreSessionPromptWrapper as PreSessionPrompt,
  PreSessionPromptVariant,
} from './pre-session-prompt/PreSessionPrompt';
import { usePreSessionPromptState } from './pre-session-prompt/usePreSessionPromptState';
import { useTransitionToMeetingRoom } from './useWatchForRoomToken';

const PRE_SESSION_PROMPT_THRESHOLD_MINUTES = 4;

type SessionInfoSectionWrapperProps = {
  appointment?: TelehealthSessionInfoData_TelehealthSessionInfo_Appointment | null;
  formsAreLoading?: boolean;
  hasForms?: boolean;
  openDrawer: (state: VisitorDrawerState) => void;
};

function isWithinThreshold(appointmentStartTime?: string) {
  if (!appointmentStartTime) return false;
  const isWithinThreshold =
    differenceInMinutes(parseISO(appointmentStartTime), new Date()) > PRE_SESSION_PROMPT_THRESHOLD_MINUTES;
  return isWithinThreshold;
}

function appointmentHasStarted(appointmentStartTime?: string) {
  if (!appointmentStartTime) return false;
  return Date.now() > new Date(appointmentStartTime).getTime();
}
export function SessionInfoSectionWrapper({ appointment, hasForms, ...props }: SessionInfoSectionWrapperProps) {
  const { shouldShowReflectionTopics, isLoading: isLoadingShouldShowReflectionTopics } =
    useGetShouldShowReflectionTopics();
  const isLoading = useContext(StatsigLoadingContext) || isLoadingShouldShowReflectionTopics;
  const { preSessionPrompt, setPreSessionPrompt, clearPreSessionPrompt } = usePreSessionPromptState();
  const hasClearedLocalStorageRef = useRef(false); // to prevent re-computation
  const deadline = useAtomValue(deadlineAtom);
  const hasMood = !!useAtomValue(recordedMoodCheckin);
  const { value: isPreSessionReflectionsEnabled } = useFeatureGate(preSessionReflectionsGate);
  const { value: is4MinRestrictionDisabled } = useFeatureGate(telehealth4MinMeasureRestrictionDisabledGate);
  const { value: isAutoOpenMeasuresDrawerEnabled } = useFeatureGate(telehealthAutoOpenIncompleteMeasuresDrawer);

  const measures = useAtomValue(visitorMeasuresAtom) ?? [];
  const visitorMeasuresAreAllScreeners =
    measures.length > 0 &&
    measures.reduce(
      (prev, currMeasure) =>
        prev &&
        !!currMeasure &&
        (currMeasure.schema.type === MeasureSchemaType.GAD7_WITH_SCREENER ||
          currMeasure.schema.type === MeasureSchemaType.PHQ9_WITH_SCREENER),
      true,
    );

  const transitionToMeetingRoom = useTransitionToMeetingRoom();
  const getHasCompletedClientMeasures = useAtomCallback(useCallback((get) => get(hasCompletedClientMeasuresAtom), []));
  const getDoesClientRequireSafetyGuards = useAtomCallback(
    useCallback((get) => get(doesClientRequireSafetyGuardsAtom), []),
  );

  const joinCallback = useCallback(() => {
    transitionToMeetingRoom({
      onDone() {
        hasClearedLocalStorageRef.current = true;
        clearPreSessionPrompt();
      },
    });
  }, [clearPreSessionPrompt, transitionToMeetingRoom]);

  useEffect(
    function allowPreSessionPromptToRecompute() {
      if (!hasMood) return;

      clearPreSessionPrompt();
    },
    [clearPreSessionPrompt, hasMood],
  );

  useEffect(
    function setPreSessionPromptState() {
      if (hasClearedLocalStorageRef.current) return;
      const hasAppointment = !!appointment?.timeStart;
      const doesClientRequireSafetyGuards = getDoesClientRequireSafetyGuards();
      const hasCompletedClientMeasures = getHasCompletedClientMeasures();
      const shouldShowMoodSurvey =
        !hasMood &&
        !appointmentHasStarted(appointment?.timeStart) &&
        hasAppointment &&
        !doesClientRequireSafetyGuards &&
        // If the user has completed the measures, then mood will be skipped once the reflections feature is enabled
        (!isPreSessionReflectionsEnabled || !hasCompletedClientMeasures);

      const shouldShowScreener = !appointmentHasStarted(appointment?.timeStart) && visitorMeasuresAreAllScreeners;

      const shouldShowClientMeasures =
        !isAutoOpenMeasuresDrawerEnabled &&
        (is4MinRestrictionDisabled ||
          (hasAppointment && isWithinThreshold(appointment?.timeStart)) ||
          shouldShowScreener) &&
        hasForms &&
        !doesClientRequireSafetyGuards;

      // When the auto-open measures drawer feature gate is enabled, we no
      // longer show any CTA for measures in the pre-session prompt. If that
      // feature gate is disabled, however, then we show the client measures CTA
      // if the 4-min restriction is disabled OR if the appointment has started
      // and the time threshold is met or if the client has an appointment and
      // the screener is visible.
      // 1. Measures is always highest priority, then...
      // 2. Mood Check-in (if the user has not completed the mood check-in and
      //    the appointment has not started)
      // 3. Reflection Topics and vibes (if the user is authenticated and the
      //    feature gate is enabled and the user has reflection   topics)
      // 4. Vibes (if no other pre-session prompt is shown). This is the only
      //    option for clients who require safety guards, who are not eligible
      //    to fill out any forms in the Telehealth waiting room.

      if (preSessionPrompt !== undefined) {
        // once set, always set
        return;
      } else if (shouldShowClientMeasures) {
        setPreSessionPrompt(PreSessionPromptVariant.CLIENT_MEASURES);
      } else if (shouldShowMoodSurvey) {
        setPreSessionPrompt(PreSessionPromptVariant.MOOD_SURVEY);
      } else if (shouldShowReflectionTopics && hasAppointment && !doesClientRequireSafetyGuards) {
        setPreSessionPrompt(PreSessionPromptVariant.REFLECTION_TOPICS);
      } else {
        setPreSessionPrompt(PreSessionPromptVariant.VIBES);
      }
    },
    [
      isAutoOpenMeasuresDrawerEnabled,
      is4MinRestrictionDisabled,
      appointment?.timeStart,
      hasForms,
      hasMood,
      preSessionPrompt,
      setPreSessionPrompt,
      visitorMeasuresAreAllScreeners,
      getHasCompletedClientMeasures,
      isPreSessionReflectionsEnabled,
      shouldShowReflectionTopics,
      getDoesClientRequireSafetyGuards,
    ],
  );

  if (!isLoading && preSessionPrompt !== undefined) {
    return (
      <div className='flex flex-col items-center gap-y-2 p-4'>
        {Statsig.checkGate(delayedEntryGate) && deadline && (
          <JoinSessionCallout deadline={deadline} joinCallback={joinCallback} />
        )}
        <PreSessionPrompt variant={preSessionPrompt} {...props} />
      </div>
    );
  }
}
